import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import {
  Plus, Pencil, Trash2, MoreHorizontal, Loader2, CreditCard, Landmark, HandCoins, TrendingDown, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);

const LIABILITY_TYPES = [
  { value: 'credit_card', label: 'Credit Card', icon: CreditCard },
  { value: 'loan', label: 'Loan', icon: HandCoins },
  { value: 'mortgage', label: 'Mortgage', icon: Landmark },
  { value: 'other', label: 'Other', icon: TrendingDown },
];

const TYPE_COLORS = { credit_card: '#EF4444', loan: '#F59E0B', mortgage: '#6366F1', other: '#6B7280' };

const SLUG_TO_TYPE = { 'credit-cards': 'credit_card', loans: 'loan', mortgages: 'mortgage' };

function LiabilityDialog({ open, onOpenChange, item, companyId, onSave, defaultType }) {
  const [form, setForm] = useState({ name: '', liability_type: 'credit_card', balance: '', credit_limit: '', interest_rate: '', minimum_payment: '', due_date: '', institution: '', account_number: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (item) {
        setForm({ name: item.name || '', liability_type: item.liability_type || 'credit_card', balance: String(item.balance || ''), credit_limit: item.credit_limit != null ? String(item.credit_limit) : '', interest_rate: item.interest_rate != null ? String(item.interest_rate) : '', minimum_payment: item.minimum_payment != null ? String(item.minimum_payment) : '', due_date: item.due_date || '', institution: item.institution || '', account_number: item.account_number || '', notes: item.notes || '' });
      } else {
        setForm({ name: '', liability_type: defaultType || 'credit_card', balance: '', credit_limit: '', interest_rate: '', minimum_payment: '', due_date: '', institution: '', account_number: '', notes: '' });
      }
    }
  }, [item, open, defaultType]);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form, balance: parseFloat(form.balance) || 0, company_id: companyId,
        credit_limit: form.credit_limit ? parseFloat(form.credit_limit) : null,
        interest_rate: form.interest_rate ? parseFloat(form.interest_rate) : null,
        minimum_payment: form.minimum_payment ? parseFloat(form.minimum_payment) : null,
        due_date: form.due_date || null, institution: form.institution || null,
        account_number: form.account_number || null, notes: form.notes || null,
      };
      if (item) {
        await axios.put(`${API}/api/liabilities/${item.id}`, payload, { withCredentials: true });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/api/liabilities`, payload, { withCredentials: true });
        toast.success('Added');
      }
      onSave(); onOpenChange(false);
    } catch { toast.error('Failed to save'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{item ? 'Edit Liability' : 'Add Liability'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g., Chase Sapphire" data-testid="liability-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <Select value={form.liability_type} onValueChange={v => setForm(p => ({...p, liability_type: v}))}>
                <SelectTrigger data-testid="liability-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LIABILITY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Balance Owed</Label>
              <Input type="number" step="0.01" value={form.balance} onChange={e => setForm(p => ({...p, balance: e.target.value}))} placeholder="0.00" data-testid="liability-balance-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Credit Limit</Label>
              <Input type="number" step="0.01" value={form.credit_limit} onChange={e => setForm(p => ({...p, credit_limit: e.target.value}))} placeholder="0.00" />
            </div>
            <div>
              <Label>Interest Rate (%)</Label>
              <Input type="number" step="0.01" value={form.interest_rate} onChange={e => setForm(p => ({...p, interest_rate: e.target.value}))} placeholder="0.00" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Min. Payment</Label>
              <Input type="number" step="0.01" value={form.minimum_payment} onChange={e => setForm(p => ({...p, minimum_payment: e.target.value}))} placeholder="0.00" />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input value={form.due_date} onChange={e => setForm(p => ({...p, due_date: e.target.value}))} placeholder="e.g., 15th of month" />
            </div>
          </div>
          <div>
            <Label>Institution</Label>
            <Input value={form.institution} onChange={e => setForm(p => ({...p, institution: e.target.value}))} placeholder="e.g., Chase, Wells Fargo" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Account # (last 4)</Label>
              <Input value={form.account_number} onChange={e => setForm(p => ({...p, account_number: e.target.value}))} placeholder="****1234" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Optional" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="save-liability-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : item ? 'Update' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LiabilitiesPage() {
  const { currentCompany } = useCompany();
  const { typeSlug } = useParams();
  const filterType = SLUG_TO_TYPE[typeSlug] || null;
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const params = { company_id: currentCompany.id };
      if (filterType) params.liability_type = filterType;
      const [itemsRes, sumRes] = await Promise.all([
        axios.get(`${API}/api/liabilities`, { params, withCredentials: true }),
        axios.get(`${API}/api/liabilities/summary`, { params: { company_id: currentCompany.id }, withCredentials: true })
      ]);
      setItems(itemsRes.data);
      setSummary(sumRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [currentCompany, filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/liabilities/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Deleted'); setDeleteTarget(null); fetchData();
    } catch { toast.error('Failed'); }
  };

  const pageTitle = filterType
    ? LIABILITY_TYPES.find(t => t.value === filterType)?.label + 's'
    : 'Liabilities';

  if (!currentCompany) return null;
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>;

  const pieData = summary?.by_type?.filter(t => t.value > 0) || [];

  return (
    <div className="space-y-6" data-testid="liabilities-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{pageTitle}</h1>
          <p className="text-gray-500 text-sm mt-0.5">Track what you owe</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-liability-btn">
          <Plus className="w-4 h-4 mr-2" />Add {filterType ? LIABILITY_TYPES.find(t => t.value === filterType)?.label : 'Liability'}
        </Button>
      </div>

      {/* Summary - only on main page */}
      {!filterType && summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-red-900 to-red-800 rounded-2xl border border-gray-200 p-6">
            <p className="text-red-200 text-sm">Total Liabilities</p>
            <p className="text-4xl font-bold text-white mt-1" data-testid="total-liabilities">{fmtCurrency(summary.total_liabilities)}</p>
            <p className="text-red-300 text-xs mt-1">{summary.count} accounts</p>
          </div>
          {pieData.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Breakdown</h3>
              <div className="flex items-center gap-4">
                <div className="w-36 h-36">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="type" cx="50%" cy="50%" innerRadius={30} outerRadius={55} strokeWidth={2} stroke="#fff">
                        {pieData.map((entry, i) => <Cell key={i} fill={TYPE_COLORS[entry.type] || '#6B7280'} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtCurrency(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {pieData.map((t, i) => {
                    const info = LIABILITY_TYPES.find(lt => lt.value === t.type);
                    return (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TYPE_COLORS[t.type] }} />
                          <span className="text-gray-600">{info?.label || t.type}</span>
                        </div>
                        <span className="text-gray-900 font-medium">{fmtCurrency(t.value)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              {!filterType && <TableHead>Type</TableHead>}
              <TableHead>Institution</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead className="text-right">Limit</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Min. Payment</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={filterType ? 8 : 9} className="text-center py-12 text-gray-400">
                <TrendingDown className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                <p className="font-medium">No {pageTitle.toLowerCase()} yet</p>
              </TableCell></TableRow>
            ) : items.map(l => {
              const info = LIABILITY_TYPES.find(t => t.value === l.liability_type);
              const Icon = info?.icon || TrendingDown;
              return (
                <TableRow key={l.id} className="hover:bg-gray-50/50" data-testid={`liability-row-${l.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${TYPE_COLORS[l.liability_type]}15` }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: TYPE_COLORS[l.liability_type] }} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{l.name}</p>
                        {l.account_number && <p className="text-xs text-gray-400">{l.account_number}</p>}
                      </div>
                    </div>
                  </TableCell>
                  {!filterType && <TableCell><span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${TYPE_COLORS[l.liability_type]}15`, color: TYPE_COLORS[l.liability_type] }}>{info?.label}</span></TableCell>}
                  <TableCell className="text-sm text-gray-500">{l.institution || '-'}</TableCell>
                  <TableCell className="text-right font-semibold text-red-600">{fmtCurrency(l.balance)}</TableCell>
                  <TableCell className="text-right text-sm text-gray-500">{l.credit_limit != null ? fmtCurrency(l.credit_limit) : '-'}</TableCell>
                  <TableCell className="text-right text-sm text-gray-500">{l.interest_rate != null ? `${l.interest_rate}%` : '-'}</TableCell>
                  <TableCell className="text-right text-sm text-gray-500">{l.minimum_payment != null ? fmtCurrency(l.minimum_payment) : '-'}</TableCell>
                  <TableCell className="text-sm text-gray-500">{l.due_date || '-'}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditItem(l)}><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteTarget(l)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <LiabilityDialog open={addOpen || !!editItem} onOpenChange={v => { if (!v) { setAddOpen(false); setEditItem(null); } }}
        item={editItem} companyId={currentCompany?.id} onSave={fetchData} defaultType={filterType} />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Liability</AlertDialogTitle>
            <AlertDialogDescription>Delete "{deleteTarget?.name}"?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
